import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Spin, Typography, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { IconArrowLeft, IconHeartOff } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import { getMyFavorites } from '../../services/FavoriteService';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import HeavyDutyMachineryCard from './HeavyDutyMachineryCard';

const { Title } = Typography;

// Reached from Profile's own "Favoritos" row (see ProfileMobile) -- its own
// back arrow always returns there, same convention Settings' sub-pages use
// for /account.
const FavoritesHeader: React.FC<{ title: string; onBack: () => void }> = ({ title, onBack }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 4px' }}>
      <button
        type="button"
        onClick={onBack}
        aria-label={translate(t, TRANSLATIONS.BACK)}
        style={{ border: 'none', background: 'none', padding: 8, margin: '-8px 0 -8px -8px', cursor: 'pointer', display: 'flex', color: token.colorText }}
      >
        <IconArrowLeft size={22} />
      </button>
      <Title level={4} style={{ margin: 0 }}>
        {title}
      </Title>
    </div>
  );
};

/**
 * Every space the caller has saved, most-recently-saved first -- cursor
 * pagination loaded a page at a time as the sentinel below scrolls into
 * view, same nextCursor convention as /heavy-duty-machinery's own search results.
 */
const HeavyDutyMachineryFavorites: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState<IHeavyDutyMachinery[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    getMyFavorites()
      .then((res) => {
        setSpaces(res.items);
        setCursor(res.nextCursor);
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITES_LOAD_ERROR)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursorRef.current || loadingMoreRef.current) return;
    setLoadingMore(true);
    try {
      const res = await getMyFavorites(cursorRef.current);
      setSpaces((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITES_LOAD_ERROR));
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, spaces.length]);

  const handleRemoved = (spaceId: string) => setSpaces((prev) => prev.filter((s) => s.id !== spaceId));

  return (
    <AppLayout hideFooter>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        <FavoritesHeader title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITES_TITLE)} onBack={() => navigate('/account')} />

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : spaces.length === 0 ? (
          <EmptyState
            icon={<IconHeartOff size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITES_EMPTY)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITES_EMPTY_DESCRIPTION)}
          />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {spaces.map((space) => (
                <Col xs={24} sm={12} md={8} key={space.id}>
                  <HeavyDutyMachineryCard
                    space={space}
                    onClick={() => navigate(`/heavy-duty-machinery/${space.id}`)}
                    onUnfavorited={() => handleRemoved(space.id)}
                  />
                </Col>
              ))}
            </Row>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default HeavyDutyMachineryFavorites;
